import _regeneratorRuntime from "babel-runtime/regenerator";

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

import { autorun, toJS } from "mobx";
import { types, getParentOfType, getSnapshot, applySnapshot, onSnapshot as _onSnapshot, flow } from "mobx-state-tree";
import { get, isArray, isNil, isNumber, trim } from "lodash-es";
import { generateId, getFormattedValue, hasCircularDependency, isFormula, standardizeInput, validateNodeName } from "../core/utils";
import { simplifyTokens, tokenize } from "../core/parser";
import { errors, NODE_TYPES, FIELD_TYPES, NODE_NAME_MAX_LENGTH } from "../core/common";
import { Store, ValidationError } from "./Store";

export var FormulaModel = types.model("FormulaModel", {
  id: types.optional(types.identifier, function () {
    return generateId();
  }),
  type: NODE_TYPES.formula,
  name: types.string,
  value: types.frozen(null),
  formatted: types.frozen(null),
  formatter: types.maybeNull(types.string),
  fieldType: FIELD_TYPES.plain,
  fieldMetaData: types.frozen(null)
  /*
  uri: types.maybeNull(types.string),
  responsePath: types.maybeNull(types.string),
  mergeFieldData: types.frozen(null),
  sliderVisible: false
  */
}).volatile(function () {
  return {
    isEditing: false
  };
}).views(function (self) {
  return {
    get store() {
      return getParentOfType(self, Store);
    },
    get isPlainField() {
      return self.fieldType === FIELD_TYPES.plain;
    },
    get isSliderField() {
      return self.fieldType === FIELD_TYPES.slider;
    },
    get isMergeField() {
      return self.fieldType === FIELD_TYPES.merge;
    },
    get isFormula() {
      return isFormula(self.value);
    },
    get isNumber() {
      return isNumber(self.value);
    },
    get parsed() {
      if (self.isMergeField) {
        var name = self.fieldMetaData.name;

        var rowCol = self.store.dataSourceFields.find(function (_ref) {
          var label = _ref.label;
          return label === name;
        });
        var effectiveValue = rowCol.value;
        return {
          result: effectiveValue === undefined ? null : effectiveValue,
          error: null
        };
      }

      if (!self.isFormula) {
        return { result: self.value, error: null };
      }

      return self.store.parser.parseAsTokens(self.tokens);
    },
    get tokens() {
      if (!self.isFormula) {
        return [];
      }

      return simplifyTokens(tokenize(self.value), self.store);
    },
    get _formatted() {
      var _self$parsed = self.parsed,
          result = _self$parsed.result,
          error = _self$parsed.error;


      if (error) {
        return error;
      }

      if (isArray(result)) {
        return result.map(function (r) {
          return getFormattedValue(r, self.formatter);
        });
      }

      return getFormattedValue(result, self.formatter);
    },
    serialize: function serialize() {
      var toJSON = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;

      return toJSON ? getSnapshot(self) : toJS(self);
    },
    onSnapshot: function onSnapshot(callback) {
      return _onSnapshot(self, callback);
    }
  };
}).actions(function (self) {
  var formattedDisposer = void 0;
  var uriDisposer = void 0;

  return {
    setName: function setName(_name) {
      var oldName = self.name;
      var name = trim(_name);

      if (oldName === name) {
        return;
      }

      if (get(name, "length") > NODE_NAME_MAX_LENGTH) {
        throw new ValidationError("name", errors.name_too_long);
      }

      if (!validateNodeName(name)) {
        // ToDo: change to formula_name
        throw new ValidationError("name", errors.name_invalid);
      }

      var existingNode = self.store.findByName(name);
      if (existingNode && existingNode.id !== self.id) {
        throw new ValidationError("name", errors.name_exists);
      }

      self.name = name;
      self.store.renameNode(self, oldName, name);
    },
    setValue: function setValue(_value) {
      var value = standardizeInput(_value);

      if (!isNil(value) && isFormula(value) && hasCircularDependency(self.name, value)) {
        throw new ValidationError("value", errors.circular_dependency);
      }

      self.value = value;
    },
    setFormatted: function setFormatted(formatted) {
      self.formatted = formatted;
    },
    setFormatter: function setFormatter(formatter) {
      self.formatter = formatter;
    },
    setFieldType: function setFieldType(fieldType) {
      self.fieldType = fieldType;
    },
    setFieldMetaData: function setFieldMetaData(fieldMetaData) {
      self.fieldMetaData = fieldMetaData;
    },
    toggleEditing: function toggleEditing() {
      var isEditing = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : !self.isEditing;
      var _self$store = self.store,
          editingNode = _self$store.editingNode,
          isSomeNodeEditing = _self$store.isSomeNodeEditing;


      if (isSomeNodeEditing && !self.isEditing) {
        editingNode.toggleEditing(false);
      }

      self.isEditing = isEditing;
    },
    toggleSliderVisible: function toggleSliderVisible() {
      var sliderVisible = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : !self.sliderVisible;

      self.sliderVisible = sliderVisible;
    },
    setProps: function setProps(props) {
      if (!props) return;

      applySnapshot(self, _extends({}, self.serialize(), props));
    },
    handleSave: function handleSave(newValue, newName, newFormatter, newFieldType, fieldMetaData) {
      var name = self.name,
          formatter = self.formatter,
          fieldType = self.fieldType,
          setValue = self.setValue,
          setName = self.setName,
          setFormatter = self.setFormatter,
          setFieldType = self.setFieldType,
          setFieldMetaData = self.setFieldMetaData,
          toggleEditing = self.toggleEditing;


      if (name !== newName) {
        setName(newName);
      }

      if (formatter !== newFormatter) {
        setFormatter(newFormatter);
      }

      if (fieldType !== newFieldType) {
        setFieldType(newFieldType);
      }

      setFieldMetaData(fieldMetaData);
      setValue(newValue);
      toggleEditing(false);
    },

    fetchAsyncData: flow( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var response, json, value;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              if (self.uri) {
                _context.next = 3;
                break;
              }

              self.responsePath = null;
              return _context.abrupt("return");

            case 3:
              _context.next = 5;
              return fetch(self.uri);

            case 5:
              response = _context.sent;
              _context.next = 8;
              return response.json();

            case 8:
              json = _context.sent;
              value = get(json, self.responsePath);

              self.setValue(value);

            case 11:
            case "end":
              return _context.stop();
          }
        }
      }, _callee, this);
    })),
    destroy: function destroy() {
      self.store.removeNode(self);
    },
    afterCreate: function afterCreate() {
      formattedDisposer = autorun(function () {
        self.setFormatted(self._formatted);
      });

      /*
      uriDisposer = autorun(() => {
        self.fetchAsyncData();
      });
      */
    },
    beforeDestroy: function beforeDestroy() {
      if (formattedDisposer) {
        formattedDisposer();
      }

      /*
      if (uriDisposer) {
        uriDisposer();
      }
      */
    }
  };
});